import { Navigate } from 'react-router-dom';
import {
  Truck,
  PhoneOutgoing,
  BadgeCheck,
  ShieldAlert,
  ClipboardList,
  CalendarClock,
  Search
} from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

// FREIGHT BROKER DISPATCH — Stage 1 admin surface (owner-only).
//
// The page the owner uses to SEE and troubleshoot the broker-dispatch product:
// the call flow (offer → ask MC# → VERIFY → tender → check-back), the FMCSA
// verify step + its current PLACEHOLDER status, the load fields the agent
// tenders, and exactly how to run a test call. It reads from static config that
// mirrors the seeded "Freight Broker (Demo)" tenant/campaign so the owner can
// review layout/design before the real FMCSA webKey and editable persistence
// land. Gated client-side (redirect non-owners); the data here is non-sensitive.

const FLOW_STAGES = [
  {
    icon: PhoneOutgoing,
    stage: '1 · Offer',
    title: 'Offer the load (the hook)',
    body: 'The agent opens with the lane + equipment only — enough to hook, not the full tender. "Dry van out of Dallas heading to Phoenix, picking up tomorrow — you running anything out that way?"'
  },
  {
    icon: Search,
    stage: '2 · Ask MC#',
    title: 'Ask the carrier’s MC number',
    body: 'Before any rate or details: "Before I send you the rate con, I just need to verify your authority — what’s your MC number?" The agent reads it back to confirm, accepts a DOT# too.'
  },
  {
    icon: BadgeCheck,
    stage: '3 · Verify',
    title: 'Verify operating authority (FMCSA)',
    body: 'The hard gate. The agent hands the MC# to the verification service and branches on the result — authorized, out-of-service, not-found, or unverifiable. It never tenders a load it could not verify.'
  },
  {
    icon: ClipboardList,
    stage: '4 · Tender',
    title: 'Tender the load (authorized only)',
    body: 'Only for an authorized carrier: full pickup/delivery windows, weight/commodity, equipment, special reqs, and the rate. Negotiates within the posted floor/ceiling and captures the agreed rate.'
  },
  {
    icon: CalendarClock,
    stage: '5 · Check-back',
    title: 'Set a status check-back',
    body: 'On a booked load, set expectations for the status check ("I’ll check in once you’re loaded, and again at delivery") and confirm the best number. A later CHECK_STATUS call follows near the windows.'
  }
];

// The four verify branches — exactly how the agent acts on each result. Mirrors
// freight_verify.verification_directive in the panel agent.
const VERIFY_BRANCHES = [
  { label: 'AUTHORIZED', tone: 'good', body: 'Confirm the carrier by name, then proceed to tender the full load + rate.' },
  { label: 'OUT OF SERVICE', tone: 'bad', body: 'Politely decline — no load, no rate. Tell them to call back once authority is cleared.' },
  { label: 'NOT FOUND', tone: 'warn', body: 'Ask them to re-read the MC number once. Still no match → setup follows up, close.' },
  { label: 'UNVERIFIABLE', tone: 'warn', body: 'Lookup is slow — do NOT tender. Defer to a human callback so the carrier isn’t held up.' }
];

const LOAD_FIELDS = [
  { label: 'Lane (origin → destination)', value: 'Dallas, TX → Phoenix, AZ' },
  { label: 'Equipment', value: 'Dry van' },
  { label: 'Weight / commodity', value: '45,000 lbs · packaged goods' },
  { label: 'Pickup window', value: 'Tomorrow 8:00 AM' },
  { label: 'Delivery window', value: 'Thursday by noon' },
  { label: 'Posted rate', value: '$2,400 all-in' }
];

function toneClasses(tone) {
  if (tone === 'good') return 'border-emerald-700/50 text-emerald-300';
  if (tone === 'bad') return 'border-red-700/50 text-red-300';
  return 'border-amber-600/50 text-amber-300';
}

export default function FreightBroker() {
  const { isOwner } = useAuth();
  if (!isOwner) return <Navigate to="/" replace />;

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Freight Broker"
        subtitle="Broker dispatch — the AI calls carriers to cover loads: offer → verify authority → tender → check-back"
        actions={
          <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-amber-600/50 text-amber-300">
            <ShieldAlert size={14} /> FMCSA verify: PLACEHOLDER (mock)
          </span>
        }
      />

      {/* FMCSA placeholder banner — the load-bearing Stage-1 caveat. */}
      <div className="card border-amber-600/40 mb-8">
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-amber-300 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-stone-100 font-medium mb-1">Authority verification is in placeholder mode</h3>
            <p className="text-sm text-stone-400">
              No FMCSA <code className="text-stone-300">webKey</code> is configured yet, so the verify step returns a
              realistic <span className="text-amber-300">simulated</span> result (carriers verify as authorized by
              default, and special MC numbers can demo the other branches). The whole flow runs end-to-end today.
              The moment the FMCSA <code className="text-stone-300">webKey</code> is added (set
              <code className="text-stone-300"> FMCSA_WEBKEY</code> on core), it flips to the live FMCSA QCMobile API —
              no other change. Every verification is logged as <code className="text-stone-300">[MOCK]</code> until then.
            </p>
          </div>
        </div>
      </div>

      {/* The call flow. */}
      <h2 className="text-stone-200 font-medium mb-3 flex items-center gap-2">
        <Truck size={18} className="text-alfred-gold" /> The broker-dispatch call flow
      </h2>
      <ol className="space-y-3 mb-10">
        {FLOW_STAGES.map(({ icon: Icon, stage, title, body }) => (
          <li key={stage} className="card">
            <div className="flex items-start gap-3">
              <Icon size={18} className="text-alfred-gold mt-1 shrink-0" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-0.5">{stage}</div>
                <h3 className="text-stone-100 font-medium mb-1">{title}</h3>
                <p className="text-sm text-stone-400">{body}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      {/* The verify branch table. */}
      <h2 className="text-stone-200 font-medium mb-3 flex items-center gap-2">
        <BadgeCheck size={18} className="text-alfred-gold" /> Verify step — how the agent branches
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
        {VERIFY_BRANCHES.map(({ label, tone, body }) => (
          <div key={label} className={`card ${toneClasses(tone)}`}>
            <div className="text-xs font-semibold tracking-wide mb-1">{label}</div>
            <p className="text-sm text-stone-400">{body}</p>
          </div>
        ))}
      </div>

      {/* The load fields the agent tenders. */}
      <h2 className="text-stone-200 font-medium mb-3 flex items-center gap-2">
        <ClipboardList size={18} className="text-alfred-gold" /> Load fields (this demo’s sample load)
      </h2>
      <div className="card mb-10">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {LOAD_FIELDS.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-[11px] uppercase tracking-wider text-stone-500">{label}</dt>
              <dd className="text-sm text-stone-200">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-stone-500 mt-4">
          These ride on the demo campaign’s prompt. Per-call loads (from a TMS / load board / upload) will populate
          this once the freight data model lands — Stage 1 uses one fixed sample load so you can hear a full tender.
        </p>
      </div>

      {/* How to test. */}
      <h2 className="text-stone-200 font-medium mb-3 flex items-center gap-2">
        <PhoneOutgoing size={18} className="text-alfred-gold" /> How to test it
      </h2>
      <div className="card">
        <ol className="text-sm text-stone-400 space-y-2 list-decimal list-inside">
          <li>
            Fire a bridged-outbound call to a number you control. It rings, and on answer the AI rep opens with the
            load offer, then asks for your MC number.
          </li>
          <li>
            Say any 5–8 digit MC number — e.g. <span className="text-stone-200">"MC one two three four five six"</span>.
            It verifies (mock) and tenders the load.
          </li>
          <li>
            To hear the other branches, end the MC number with a special digit:
            <span className="text-stone-200"> …7 = out of service</span>,
            <span className="text-stone-200"> …4 = not found</span>,
            <span className="text-stone-200"> …0/9 = unverifiable</span>. Anything else verifies as authorized.
          </li>
        </ol>
        <p className="text-xs text-stone-500 mt-4">
          The owner fires the test call via the Telnyx bridge originate endpoint (campaign mode) — see the runbook in
          the deploy report. The demo tenant is <span className="text-stone-300">Freight Broker (Demo)</span>; its
          campaign is <span className="text-stone-300">Broker Dispatch (Demo)</span>.
        </p>
      </div>
    </div>
  );
}
